import { Component, computed, inject, input, signal, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin } from "rxjs";

import { AdaaHelper } from "../../../core/utils";
import {
  AddRuleModalComponent,
  DataTableComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormDropdownComponent,
  FormInputComponent,
  FormInputTimeComponent,
  FormTwoWaySelectComponent,
} from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, PageMode } from "../../../shared/constants/enums";
import {
  EntityModelType,
  IntegrityRuleSet,
  PropTypeModelType,
  Rule,
  TableButtonClicked,
  TriggerUpdateType,
  ValidatorModelType,
  ValueText,
} from "../../../shared/models";
import {
  EntitiesApiService,
  PropertiesService,
  SystemLayoutService,
  ValidatorApiService,
} from "../../../shared/services";
import { IntegrityApiService } from "../../../shared/services/integrity-api-service";

@Component({
  selector: "adaa-integrity-rules-editor",
  imports: [
    FormInputComponent,
    TranslatePipe,
    ReactiveFormsModule,
    FormDropdownComponent,
    FormCheckboxComponent,
    DataTableComponent,
    FormTwoWaySelectComponent,
    FormInputTimeComponent,
    FormActionButtonsComponent,
    AddRuleModalComponent,
  ],
  templateUrl: "./integrity-rules-editor.html",
  styleUrl: "./integrity-rules-editor.scss",
})
export class IntegrityRulesEditorComponent {
  private _validatorApiService = inject(ValidatorApiService);
  private _propsService = inject(PropertiesService);
  private _entitiesApiService = inject(EntitiesApiService);
  private _integrityApiService = inject(IntegrityApiService);
  private _toastrService = inject(ToastrService);
  private _translateService = inject(TranslateService);
  private _formBuilder = inject(FormBuilder);
  private _systemLayoutService = inject(SystemLayoutService);
  private _router = inject(Router);

  private _untilDestroy = AdaaHelper.untilDestroyed();
  PageMode = PageMode;

  integrityRulesForm: FormGroup = new FormGroup({});
  rulesetTypes = signal<ValueText[]>([]);
  rulesetSeverities = signal<ValueText[]>([]);
  activationTypes = signal<ValueText[]>([]);
  scopes = signal<ValueText[]>([]);
  days = signal<(ValueText & { isActive?: boolean })[]>([]);
  periods = signal<(ValueText & { isActive?: boolean })[]>([]);
  ruleTypes = signal<PropTypeModelType[]>([]);
  entities = signal<EntityModelType[]>([]);
  entityOptions = computed(() =>
    AdaaHelper.setDropdownArray(this.entities(), "id", AdaaHelper.getFieldLanguage("name"))
  );
  actions = signal<ValueText[]>([]);
  objectTypes = signal<ValueText[]>([]);
  integrityRule = signal<IntegrityRuleSet>({
    id: 0,
    nameAE: "",
    nameEN: "",
    dscAE: "",
    dscEN: "",
    scope: 0,
    key: "",
    severity: 0,
    type: 0,
    combinationMode: 0,
    snooze: "",
    status: 0,
    rules: [],
    triggers: [],
    entities: [],
    cron: "",
    scheduleType: "",
    scheduleDays: [],
    affectedEntities: [],
    scheduleTime: "",
  });
  Constants = Constants;
  id = input.required<string>();

  formValidation = signal<ValidatorModelType | undefined>(undefined);
  pageMode = input.required<PageMode>();
  submitted = signal<boolean>(false);
  triggers = signal<TriggerUpdateType[]>([]);
  filteredTriggers = computed(() => this.triggers().filter((t) => t.statusId !== Constants.OBJECT_STATUS.REMOVE));
  rules = signal<Rule[]>([]);
  filteredRules = computed(() => this.rules().filter((rule) => rule.status !== Constants.OBJECT_STATUS.REMOVE));
  addNewRuleModal = viewChild.required<AddRuleModalComponent>("addRule");

  selectionInvalid = computed(() => {
    if (this.submitted() && this.typeChanged()) {
      if (this.typeChanged() === Constants.CONSTANT_RULES_MANAGEMENT_TRIGGER_PROP) {
        return this.triggers().length === 0;
      } else if (this.typeChanged() === Constants.CONSTANT_RULES_MANAGEMENT_CALENDAR_PROP) {
        return (
          !this.days().find((d) => d.isActive) ||
          this.integrityRulesForm.get("scheduleTime")?.value === null ||
          this.integrityRulesForm.get("scope")?.value === null
        );
      }
    }
    return false;
  });

  typeChanged = signal<number | string>(0);

  public get displayLabel(): string {
    const nameEN = this.integrityRulesForm.get("nameEN")?.value;
    const nameAE = this.integrityRulesForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this._prepareForm();
    this._initPage();
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroy()).subscribe({
      next: () => {
        if (AdaaHelper.entity.id !== Constants.CONSTANT_PMO_ID) {
          this._router.navigateByUrl("/console");
        }
      },
    });
  }

  private _prepareForm() {
    this.integrityRulesForm = this._formBuilder.group({
      nameAE: null,
      nameEN: null,
      dscAE: null,
      dscEN: null,
      type: null,
      severity: null,
      combinationMode: null,
      snooze: null,
      scope: Constants.CONSTANT_RULES_MANAGEMENT_SCOPE_GENERAL,
      triggerConfigId: null,
      actionId: null,
      scheduleTime: null,
      affectedEntities: null,
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_RULES_MANAGEMENT_TRIGGER, this.integrityRulesForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.integrityRulesForm = response.form;
        },
      });

    this.integrityRulesForm
      .get("type")
      ?.valueChanges.pipe(this._untilDestroy())
      .subscribe((value) => this.typeChanged.update(() => value));
    this.integrityRulesForm
      .get("scheduleTime")
      ?.valueChanges.pipe(this._untilDestroy())
      .subscribe((value) => this.typeChanged.update(() => value));
  }

  private _initPage(): void {
    const props = [
      Constants.CONSTANT_RULESET_TYPE,
      Constants.CONSTANT_RULESET_SEVERITY,
      Constants.CONSTANT_RULESET_ACTIVATION_TYPES,
      Constants.CONSTANT_RULESET_SCOPE,
      Constants.CONSTANT_RULESET_DAYS,
      Constants.CONSTANT_RULESET_PERIODS,
      Constants.CONSTANT_RULES_MANAGEMENT_RULETYPES_PROP,
      Constants.CONSTANT_RULES_MANAGEMENT_ACTIONS_PROP,
    ];

    forkJoin({
      ...(this.id() ? { ruleSet: this._integrityApiService.getRuleSet(+this.id()) } : {}),
      props: this._propsService.getPropByIdList(props),
      objectTypes: this._integrityApiService.getObjectTypes(),
      entities: this._entitiesApiService.getAllForEnabler(),
    }).subscribe({
      next: ({ ruleSet, props, objectTypes, entities }) => {
        const data = props.responseData;
        this.rulesetTypes.set(
          AdaaHelper.setDropdownArray(
            data.filter((e) => e.propType === Constants.CONSTANT_RULESET_TYPE),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );
        this.rulesetSeverities.set(
          AdaaHelper.setDropdownArray(
            data.filter((e) => e.propType === Constants.CONSTANT_RULESET_SEVERITY),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );
        this.activationTypes.set(
          AdaaHelper.setDropdownArray(
            data.filter((e) => e.propType === Constants.CONSTANT_RULESET_ACTIVATION_TYPES),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );
        this.scopes.set(
          AdaaHelper.setDropdownArray(
            data.filter((e) => e.propType === Constants.CONSTANT_RULESET_SCOPE),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );
        this.days.set(
          AdaaHelper.setDropdownArray(
            data.filter((e) => e.propType === Constants.CONSTANT_RULESET_DAYS),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );
        this.periods.set(
          AdaaHelper.setDropdownArray(
            data.filter((e) => e.propType === Constants.CONSTANT_RULESET_PERIODS),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );
        this.ruleTypes.set(
          data.filter(
            (e) => e.propType === Constants.CONSTANT_RULES_MANAGEMENT_RULETYPES_PROP && e.enabled === AdaaBoolean.Y
          )
        );
        this.actions.set(
          AdaaHelper.setDropdownArray(
            data.filter((e) => e.propType === Constants.CONSTANT_RULES_MANAGEMENT_ACTIONS_PROP),
            "id",
            AdaaHelper.getFieldLanguage("name")
          )
        );

        if (!objectTypes.inError) {
          this.objectTypes.set(
            AdaaHelper.setDropdownArray(objectTypes.responseData, "id", AdaaHelper.getFieldLanguage("name"))
          );
        }

        if (!entities.inError) {
          this.entities.set(entities.responseData);
        }

        if (ruleSet) {
          this._formatData(ruleSet.responseData);
        }
      },
    });
  }

  private _formatData(ruleSet: IntegrityRuleSet) {
    this.integrityRule.set(ruleSet);
    this.integrityRulesForm.patchValue({
      ...ruleSet,
      affectedEntities: ruleSet.affectedEntities?.map((entity) => entity.id),
    });

    const objectTypesMap = new Map(this.objectTypes().map((t) => [t.value, t.text]));
    const actionsMap = new Map(this.actions().map((t) => [t.value, t.text]));

    this.triggers.update((triggers) => [
      ...triggers,
      ...ruleSet.triggers.map((trigger) => ({
        ...trigger,
        object: objectTypesMap.get(trigger.triggerConfigId)!,
        action: actionsMap.get(trigger.actionId)!,
      })),
    ]);

    if (ruleSet.scheduleDays?.length) {
      const scheduleDays = ruleSet.scheduleDays;

      this.periods.update((periods) =>
        periods.map((p) => ({
          ...p,
          isActive: p.value === ruleSet.scheduleType,
        }))
      );

      this.days.update((days) =>
        days.map((d) => ({
          ...d,
          isActive: scheduleDays.includes(d.value),
        }))
      );
    }

    this.rules.set(ruleSet.rules);
  }

  public checkInvalid(control: string) {
    const type = this.integrityRulesForm.get("type")?.value;
    const value = this.integrityRulesForm.get(control)?.value;
    const formControl = this.integrityRulesForm.get(control)!;

    if (
      ["scheduleTime", "scope"].includes(control) &&
      type === Constants.CONSTANT_RULES_MANAGEMENT_CALENDAR_PROP &&
      value === null &&
      (formControl.dirty || this.submitted())
    ) {
      return true;
    }

    return (
      this.integrityRulesForm.controls[control]?.errors?.["required"] &&
      (this.integrityRulesForm.get(control)?.dirty || this.submitted())
    );
  }

  public getAction(event: TableButtonClicked) {
    switch (event.event) {
      case "view":
        this.addNewRuleModal().rule.set(event.data);
        this.addNewRuleModal().pageMode.set(PageMode.view);
        this.addNewRuleModal().open();
        break;
      case "edit":
        this.addNewRuleModal().rule.set(event.data);
        this.addNewRuleModal().pageMode.set(PageMode.edit);
        this.addNewRuleModal().open();
        break;
      case "delete":
        this._deleteRule(event.index!);
        break;

      default:
        break;
    }
  }

  public addNewRule() {
    this.addNewRuleModal().pageMode.set(PageMode.create);
    this.addNewRuleModal().open();
  }

  public selectDay(day: ValueText["value"]) {
    if (!this.periods().find((period) => period.isActive)) {
      this._toastrService.warning(this._translateService.instant("notification.warning.define_schedule"));
      return;
    }
    this.days.update((days) =>
      days.map((d) => {
        const activePeriod = this.periods().find((p) => p.isActive);

        if (activePeriod?.value === Constants.CONSTANT_RULES_MANAGEMENT_SCOPE_WEEKLY) {
          return {
            ...d,
            isActive: d.value === day ? !d.isActive : false,
          };
        }

        if (activePeriod?.value === Constants.CONSTANT_RULES_MANAGEMENT_SCOPE_DAILY) {
          return {
            ...d,
            isActive: d.value === day ? !d.isActive : d.isActive,
          };
        }

        return d;
      })
    );
  }

  public selectPeriod(period: ValueText["value"]) {
    this.days.update((currentDays) => {
      return currentDays.map((d) => ({
        ...d,
        isActive: false,
      }));
    });

    this.periods.update((currentPeriods) => {
      return currentPeriods.map((p) => ({
        ...p,
        isActive: p.value === period ? !p.isActive : false,
      }));
    });
  }

  public saveRule(rule: Rule) {
    this.rules.update((rules) => {
      if (rule.id) {
        const matchIndex = rules.findIndex((r) => r.id === rule.id);
        const updated = [...rules];
        updated.splice(matchIndex, 1, rule);
        return updated;
      }
      return [...rules, { ...rule, tempId: crypto.randomUUID(), status: Constants.OBJECT_STATUS.ACTIVE }];
    });
  }

  private _deleteRule(index: number) {
    this.rules.update((rules) => {
      if (rules[index].tempId) {
        return rules.filter((_, i) => i !== index);
      } else if (rules[index].id) {
        return rules.map((r, i) => (i === index ? { ...r, status: Constants.OBJECT_STATUS.REMOVE } : r));
      }
      return rules;
    });
  }

  public addTrigger() {
    const objectType = this.objectTypes().find(
      (obj) => obj.value === this.integrityRulesForm.get("triggerConfigId")?.value
    )!;
    const action = this.actions().find((action) => action.value === this.integrityRulesForm.get("actionId")?.value)!;

    if (
      this.triggers().find((obj) => obj.actionId === action.value) &&
      this.triggers().find((obj) => obj.triggerConfigId === objectType.value)
    )
      return;

    this.triggers.update((objects) => [
      ...objects,
      { object: objectType.text, action: action.text, triggerConfigId: objectType.value, actionId: action.value },
    ]);
  }

  public removeTrigger(trigger: TriggerUpdateType) {
    this.triggers.update((triggers) => {
      const matchIndex = triggers.findIndex(
        (t) => t.actionId === trigger.actionId && t.triggerConfigId === trigger.triggerConfigId
      );

      if (triggers[matchIndex].id) {
        triggers[matchIndex].statusId = Constants.OBJECT_STATUS.REMOVE;
      } else triggers.splice(matchIndex, 1);
      return [...triggers];
    });
  }

  public submit() {
    this.submitted.set(true);
    if (this.integrityRulesForm.invalid || !this.rules().length) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    const res = {
      ...(this.pageMode() === PageMode.edit ? { ...this.integrityRule() } : {}),
      ...this.integrityRulesForm.value,
      triggers: this.triggers().map((t) => {
        delete t.action;
        delete t.object;
        return t;
      }),
      rules: this.rules().map((r) => {
        delete r.tempId;
        return r;
      }),
    };

    if (res.scope && res.scope !== Constants.CONSTANT_RULES_MANAGEMENT_SCOPE_GENERAL) {
      res.affectedEntities = res.affectedEntities?.map((e: number) => {
        return this.entities().find((entity) => entity.id === e);
      });
    } else res.affectedEntities = null;

    res.scheduleType = this.periods().find((p) => p.isActive)?.value;
    res.scheduleDays = this.days()
      .filter((d) => d.isActive)
      .map((d) => d.value);

    this._integrityApiService.updateRuleset(res, this.pageMode()).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigateByUrl("console/rules");
      },
    });
  }

  public goBack() {
    this._router.navigateByUrl("/console/rules");
  }
}
